import React, { useEffect, useState } from 'react'
import {
  Alert,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { formatDate, formatNumber, formatPeriod } from '../../kit/helpers'
import { ALLOWANCE_METRICS } from '../tenantHelpers'
import ActionDialog, { useActionSubmit } from './ActionDialog'
import {
  METRIC_KINDS,
  metricKind,
  METRIC_LABELS,
  grantPreviewPeriods,
  isDateInput,
  dayAfter,
  toNumber,
  todayInput,
} from './constants'

const blankGrant = () => ({
  metric: '',
  amount: '',
  reason: '',
  startsAt: todayInput(),
  expiresAt: '',
})

// "Add complimentary capacity": temporary capacity AmbuNet gives on top of the
// contract. The expiry date is mandatory and exclusive; the preview lists the
// months the grant will count for before it is saved. `records` are the
// panel's monthly records: a month that is already finalised is never changed
// by a grant, so capacity that has expired and only touches finalised months
// is refused (as the server refuses it).
export const AddGrantDialog = ({ open, records = [], onClose, onSubmit }) => {
  const [form, setForm] = useState(blankGrant)
  const [attempted, setAttempted] = useState(false)
  const { busy, error, run } = useActionSubmit({ open, onSubmit, onClose })

  useEffect(() => {
    if (open) {
      setForm(blankGrant())
      setAttempted(false)
    }
  }, [open])

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

  // The server's rules (addGrant), checked before anything is sent. A blank
  // start date means "from now".
  const amount = toNumber(form.amount)
  const hours = form.metric === 'controlRoomHoursPerMonth'
  const startDay = form.startsAt || todayInput()
  // metricKind: concurrent control rooms are enforced capacity (hard), as on
  // the server, whatever the shared mirror says.
  const kind = form.metric ? metricKind(form.metric) : undefined
  const datesValid =
    (!form.startsAt || isDateInput(form.startsAt)) && isDateInput(form.expiresAt) && form.expiresAt > startDay
  const preview = datesValid ? grantPreviewPeriods(form.startsAt, form.expiresAt) : null
  const finalisedPeriods = records.filter((record) => record.finalisedAt).map((record) => record.period)
  // Monthly allowances: the months not yet finalised. Enforced capacity is
  // checked when a record is added, so only a grant that is still running counts.
  const expired = datesValid && form.expiresAt <= todayInput()
  const unaffected = preview && kind !== 'hard' ? preview.periods.filter((period) => finalisedPeriods.includes(period)) : []
  const changesNothing =
    expired && (kind === 'hard' || (preview.more === 0 && unaffected.length === preview.periods.length))
  const problems = {
    metric: form.metric ? '' : 'Choose which allowance the capacity is for.',
    amount:
      amount === null || amount <= 0
        ? 'The amount must be a number above zero.'
        : !hours && !Number.isInteger(amount)
          ? 'The amount must be a whole number.'
          : '',
    reason: form.reason.trim() ? '' : 'Enter the reason for granting this capacity.',
    startsAt: form.startsAt && !isDateInput(form.startsAt) ? 'Enter a valid start date.' : '',
    expiresAt: !isDateInput(form.expiresAt)
      ? 'Complimentary capacity must have an expiry date.'
      : form.expiresAt <= startDay
        ? 'The expiry date must be after the start date.'
        : changesNothing
          ? kind === 'hard'
            ? 'That capacity has already expired, so it would change nothing.'
            : 'That capacity has already expired and every month it covers is finalised, so it would change nothing.'
          : '',
  }
  const invalid = Object.values(problems).some(Boolean)
  const shown = (field) => (attempted ? problems[field] : '')

  const confirm = () => {
    setAttempted(true)
    if (invalid) return
    run({
      metric: form.metric,
      amount,
      reason: form.reason.trim(),
      ...(form.startsAt ? { startsAt: form.startsAt } : {}),
      expiresAt: form.expiresAt,
    })
  }

  return (
    <ActionDialog
      open={open}
      title="Add complimentary capacity"
      confirmLabel="Add capacity"
      busy={busy}
      error={error}
      onClose={onClose}
      onConfirm={confirm}
    >
      <Typography variant="body2" color="text.secondary">
        Temporary capacity on top of the contract, with no charge. It is added to the effective
        allowance while it runs and stops counting when it expires. Months that have already been
        finalised are never changed by it.
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={8}>
          <FormControl size="small" fullWidth required error={!!shown('metric')}>
            <InputLabel>Allowance</InputLabel>
            <Select value={form.metric} label="Allowance" onChange={(event) => set('metric', event.target.value)}>
              {ALLOWANCE_METRICS.map((metric) => (
                <MenuItem key={metric.key} value={metric.key}>
                  {metric.label}
                </MenuItem>
              ))}
            </Select>
            {(shown('metric') || kind) && (
              <Typography variant="caption" color={shown('metric') ? 'error' : 'text.secondary'} sx={{ mt: 0.5, mx: 1.75 }}>
                {shown('metric') || `${METRIC_KINDS[kind].label} - ${METRIC_KINDS[kind].hint}`}
              </Typography>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Amount"
            size="small"
            fullWidth
            required
            type="number"
            inputProps={{ min: 0, step: hours ? 'any' : 1 }}
            value={form.amount}
            onChange={(event) => set('amount', event.target.value)}
            error={!!shown('amount')}
            helperText={shown('amount')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Reason"
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
            value={form.reason}
            onChange={(event) => set('reason', event.target.value)}
            error={!!shown('reason')}
            helperText={shown('reason')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            type="date"
            label="Starts"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.startsAt}
            onChange={(event) => set('startsAt', event.target.value)}
            error={!!shown('startsAt')}
            helperText={shown('startsAt') || 'From the start of this day. Blank means from now.'}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            type="date"
            label="Expires"
            size="small"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: dayAfter(startDay) }}
            value={form.expiresAt}
            onChange={(event) => set('expiresAt', event.target.value)}
            error={!!shown('expiresAt')}
            helperText={shown('expiresAt') || 'Required. The capacity stops at the start of this day.'}
          />
        </Grid>
      </Grid>
      {preview && preview.periods.length > 0 && !changesNothing && (
        <Alert severity="info">
          {kind === 'hard'
            ? 'Enforced capacity counts from the start date until the expiry. Months it runs in: '
            : 'Counts in full for every calendar month it overlaps: '}
          <b>
            {preview.periods.map((period) => formatPeriod(period)).join(', ')}
            {preview.more > 0 ? ` and ${formatNumber(preview.more)} more` : ''}
          </b>
          . The expiry day itself is not included.
          {unaffected.length > 0 &&
            ` Already finalised, so unaffected: ${unaffected.map((period) => formatPeriod(period)).join(', ')}.`}
        </Alert>
      )}
    </ActionDialog>
  )
}

// Revoke one grant: it stops counting from now. Months already finalised keep
// the allowance they were assessed against.
export const RevokeGrantDialog = ({ open, grant, onClose, onSubmit }) => {
  const [reason, setReason] = useState('')
  const [attempted, setAttempted] = useState(false)
  const { busy, error, run } = useActionSubmit({ open, onSubmit, onClose })

  useEffect(() => {
    if (open) {
      setReason('')
      setAttempted(false)
    }
  }, [open])

  const problem = reason.trim() ? '' : 'Enter the reason for revoking this capacity.'
  const confirm = () => {
    setAttempted(true)
    if (problem) return
    run({ reason: reason.trim() })
  }

  return (
    <ActionDialog
      open={open}
      title="Revoke complimentary capacity"
      confirmLabel="Revoke capacity"
      confirmColor="error"
      busy={busy}
      error={error}
      onClose={onClose}
      onConfirm={confirm}
    >
      {grant && (
        <Alert severity="warning">
          +{formatNumber(grant.amount)} {METRIC_LABELS[grant.metric] ?? grant.metric} (expires{' '}
          {formatDate(grant.expiresAt)}) will stop counting from now. Months that have already
          been finalised keep the allowance they were assessed against; months still open are
          measured without it.
        </Alert>
      )}
      <TextField
        label="Reason"
        size="small"
        fullWidth
        required
        multiline
        minRows={2}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        error={attempted && !!problem}
        helperText={attempted && problem}
      />
    </ActionDialog>
  )
}
